import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSpinner,
  faUsers,
  faEye,
  faToggleOn,
  faToggleOff,
  faTrash,
} from '@fortawesome/free-solid-svg-icons';
import {
  userManagementService,
  type OrgUser,
  type CreateUserPayload,
} from '@/services/user-management.service';
import { departmentService } from '@/services/department.service';
import { rolesService } from '@/services/roles.service';
import { SkeletonPage } from '@/components/skeletons';
import { PageHeader } from '@/components/shared/PageHeader';
import { SearchBar } from '@/components/shared/SearchBar';
import { EmptyState } from '@/components/shared/EmptyState';
import { AccessRestricted } from '@/components/shared/AccessRestricted';
import { isForbiddenError } from '@/lib/is-forbidden-error';
import { Pagination } from '@/components/shared/Pagination';
import { Modal } from '@/components/shared/Modal';
import { FormField } from '@/components/shared/FormField';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { PhoneInput } from '@/components/shared/PhoneInput';
import {
  useIdentityAvailability,
  emailAvailabilityMessage,
  takenMessage,
} from './use-identity-availability';
import { SharedMailboxBadge } from './SharedMailboxBadge';
import { toast, confirmDelete } from '@/lib/swal';
import { useDebounce } from '@/hooks/use-debounce';
import { useHasPermission } from '@/hooks/use-permissions';
import { ROUTES } from '@/routes/routes';
import { isValidEmail } from '@/lib/identity';

const PAGE_SIZE = 20;

const selectClass =
  'w-full rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50';

function CreateUserForm({
  onSubmit,
  onCancel,
  saving,
}: {
  onSubmit: (data: CreateUserPayload) => void;
  onCancel: () => void;
  saving: boolean;
}) {
  const [form, setForm] = useState<Omit<CreateUserPayload, 'roleIds'> & { roleIds: string[] }>({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    username: '',
    phone: '',
    employeeCode: '',
    designation: '',
    departmentId: '',
    reportingManagerId: '',
    roleIds: [],
    isSharedEmailAccount: false,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const { data: depts } = useQuery({ queryKey: ['departments-all'], queryFn: departmentService.listAll });
  const { data: roles } = useQuery({ queryKey: ['roles'], queryFn: rolesService.listAll });
  // pageSize must stay within the endpoint's cap (`user-list-query.dto.ts`:
  // `@Max(100)`). 200 made the request 400, leaving this picker permanently
  // empty with no visible sign that anything had failed.
  const { data: managers, isError: managersFailed } = useQuery({
    queryKey: ['users-all'],
    queryFn: () => userManagementService.list({ pageSize: 100, isActive: true }),
  });

  const set =
    (field: keyof typeof form) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
      setForm((f) => ({ ...f, [field]: field === 'username' ? e.target.value.toLowerCase() : e.target.value }));

  const toggleRole = (roleId: string) =>
    setForm((f) => ({
      ...f,
      roleIds: f.roleIds.includes(roleId) ? f.roleIds.filter((r) => r !== roleId) : [...f.roleIds, roleId],
    }));

  /**
   * D-099 — Owner and Admin can never share an address: their actions must stay
   * individually attributable. Detected from the roles selected in this form so
   * the checkbox disappears the moment such a role is ticked, matching the
   * server rule rather than duplicating it.
   */
  const isPrivilegedSelection = (roles ?? [])
    .filter((r) => form.roleIds.includes(r.id))
    .some((r) => r.slug === 'owner' || r.slug === 'admin');

  // D-100 — advisory availability. Debounced, never per keystroke, and never
  // authoritative: the backend validates again on submit and its 409 decides.
  const emailState = useIdentityAvailability('email', form.email ?? '', {
    enabled: !isPrivilegedSelection || !!(form.email ?? '').trim(),
    minLength: 3,
  });
  const usernameState = useIdentityAvailability('username', form.username ?? '', { minLength: 2 });
  const phoneState = useIdentityAvailability('phone', form.phone ?? '', { minLength: 6 });
  const employeeCodeState = useIdentityAvailability('employeeCode', form.employeeCode ?? '', {
    minLength: 2,
  });

  // Toggling the checkbox re-derives this from the response already held — no
  // second request, which is the whole point of returning sharedEmail up front.
  const emailMessage = emailAvailabilityMessage(emailState, form.isSharedEmailAccount ?? false);
  const usernameTaken = takenMessage(usernameState, 'Username');
  const phoneTaken = takenMessage(phoneState, 'Mobile number');
  const employeeCodeTaken = takenMessage(employeeCodeState, 'Employee code');

  // A pending check must not be raced past; a failed one must not block.
  const checking =
    emailState.status === 'checking' ||
    usernameState.status === 'checking' ||
    phoneState.status === 'checking' ||
    employeeCodeState.status === 'checking';
  const blockingInline =
    (emailMessage?.tone === 'error' ? 1 : 0) +
    (usernameTaken ? 1 : 0) +
    (phoneTaken ? 1 : 0) +
    (employeeCodeTaken ? 1 : 0);


  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const errs: Record<string, string> = {};
    // D-099 — the required-marks here contradicted the API and are corrected
    // together with the shared-email work, because the checkbox is meaningless
    // beside a field the form wrongly insists on. The backend
    // (UserIdentityService) requires username and mobile for every employee and
    // requires email only for Owner/Admin; this form demanded the opposite.
    const email = (form.email ?? '').trim();
    if (email && !isValidEmail(email)) errs.email = 'Invalid email format';
    if (isPrivilegedSelection && !email) {
      errs.email = 'An email address is required for Owner and Admin accounts';
    }
    if (!(form.username ?? '').trim()) errs.username = 'Username is required';
    if (!(form.phone ?? '').trim()) errs.phone = 'Mobile number is required';
    if (form.password.length < 8) errs.password = 'Password must be at least 8 characters';
    if (!form.firstName.trim()) errs.firstName = 'First name is required';
    if (!form.lastName.trim()) errs.lastName = 'Last name is required';
    if (form.roleIds.length === 0) errs.roleIds = 'At least one role is required';
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    // D-100 — do not submit over a known conflict or an unfinished check. This
    // is convenience, not enforcement: the backend still rejects on write.
    if (blockingInline > 0 || checking) return;
    setErrors({});
    onSubmit({
      email: email.toLowerCase() || undefined,
      isSharedEmailAccount: isPrivilegedSelection ? false : form.isSharedEmailAccount,
      password: form.password,
      firstName: form.firstName,
      lastName: form.lastName,
      username: (form.username ?? '').trim().toLowerCase() || undefined,
      phone: form.phone || undefined,
      employeeCode: form.employeeCode || undefined,
      designation: form.designation || undefined,
      departmentId: form.departmentId || undefined,
      reportingManagerId: form.reportingManagerId || undefined,
      roleIds: form.roleIds,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <FormField label="First Name" htmlFor="u-fn" required error={errors.firstName}>
          <Input id="u-fn" value={form.firstName} onChange={set('firstName')} disabled={saving} />
        </FormField>
        <FormField label="Last Name" htmlFor="u-ln" required error={errors.lastName}>
          <Input id="u-ln" value={form.lastName} onChange={set('lastName')} disabled={saving} />
        </FormField>
      </div>
      <FormField
        label="Email"
        htmlFor="u-email"
        required={isPrivilegedSelection}
        error={errors.email ?? (emailMessage?.tone === 'error' ? emailMessage.text : undefined)}
      >
        <Input
          id="u-email"
          type="email"
          value={form.email ?? ''}
          onChange={set('email')}
          disabled={saving}
        />
        {emailMessage?.tone === 'info' && (
          <p data-testid="email-shared-info" className="mt-1.5 text-xs text-muted-foreground">
            {emailMessage.text}
          </p>
        )}
      </FormField>
      {isPrivilegedSelection ? (
        <p className="-mt-2 text-xs text-muted-foreground">
          Owner and Admin accounts need their own email address, so it cannot be shared.
        </p>
      ) : (
        <label className="-mt-2 flex cursor-pointer items-start gap-2 text-sm">
          <input
            id="u-shared-email"
            type="checkbox"
            className="mt-0.5"
            checked={form.isSharedEmailAccount ?? false}
            onChange={(e) => setForm((f) => ({ ...f, isSharedEmailAccount: e.target.checked }))}
            disabled={saving}
          />
          <span>
            <span className="font-medium">Shared email account</span>
            <span className="block text-xs text-muted-foreground">
              This address may be used by more than one employee. Each person still signs in with
              their own username or mobile and their own password.
            </span>
          </span>
        </label>
      )}
      <FormField label="Password" htmlFor="u-pw" required error={errors.password}>
        <Input id="u-pw" type="password" value={form.password} onChange={set('password')} disabled={saving} autoComplete="new-password" />
      </FormField>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <FormField
          label="Username"
          htmlFor="u-uname"
          required
          error={errors.username ?? usernameTaken ?? undefined}
        >
          <Input id="u-uname" value={form.username} onChange={set('username')} disabled={saving} />
        </FormField>
        <FormField
          label="Phone"
          htmlFor="u-phone"
          required
          error={errors.phone ?? phoneTaken ?? undefined}
        >
          <PhoneInput id="u-phone" value={form.phone ?? ''} onChange={(v) => setForm((f) => ({ ...f, phone: v }))} disabled={saving} />
        </FormField>
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <FormField label="Employee Code" htmlFor="u-emp" error={employeeCodeTaken ?? undefined}>
          <Input id="u-emp" value={form.employeeCode} onChange={set('employeeCode')} placeholder="Auto-generated if blank" disabled={saving} />
        </FormField>
        <FormField label="Designation" htmlFor="u-desig">
          <Input id="u-desig" value={form.designation} onChange={set('designation')} disabled={saving} />
        </FormField>
      </div>
      <FormField label="Department" htmlFor="u-dept">
        <select id="u-dept" value={form.departmentId} onChange={set('departmentId')} disabled={saving} className={selectClass}>
          <option value="">— None —</option>
          {depts?.filter((d) => d.isActive).map((d) => (
            <option key={d.id} value={d.id}>{d.name} ({d.code})</option>
          ))}
        </select>
      </FormField>
      <FormField
        label="Reporting Manager"
        htmlFor="u-mgr"
        error={
          managersFailed ? 'Could not load the user list. Reload the page to try again.' : undefined
        }
      >
        <select id="u-mgr" value={form.reportingManagerId} onChange={set('reportingManagerId')} disabled={saving} className={selectClass}>
          <option value="">— None —</option>
          {managers?.items.map((u) => (
            <option key={u.id} value={u.id}>{u.firstName} {u.lastName} ({u.email})</option>
          ))}
        </select>
      </FormField>
      <FormField label="Roles" htmlFor="u-roles" required error={errors.roleIds}>
        <div className="flex flex-wrap gap-2 mt-1">
          {roles?.map((r) => (
            <label key={r.id} className={`flex cursor-pointer items-center gap-1.5 rounded-full border px-3 py-1 text-xs transition-colors ${form.roleIds.includes(r.id) ? 'border-primary bg-primary/10 text-primary' : 'border-border text-muted-foreground hover:border-primary/50'}`}>
              <input
                type="checkbox"
                className="sr-only"
                checked={form.roleIds.includes(r.id)}
                onChange={() => toggleRole(r.id)}
                disabled={saving}
              />
              {r.name}
            </label>
          ))}
        </div>
      </FormField>
      <div className="flex justify-end gap-3 pt-2">
        <Button type="button" variant="ghost" onClick={onCancel} disabled={saving}>Cancel</Button>
        <Button type="submit" disabled={saving} className="min-w-[100px]">
          {saving ? <FontAwesomeIcon icon={faSpinner} className="animate-spin" /> : 'Create User'}
        </Button>
      </div>
    </form>
  );
}

export function UsersPage() {
  const queryClient = useQueryClient();
  const can = useHasPermission();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 350);
  const [modalOpen, setModalOpen] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['org-users', page, debouncedSearch],
    queryFn: () => userManagementService.list({ page, pageSize: PAGE_SIZE, search: debouncedSearch || undefined }),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['org-users'] });

  const createMutation = useMutation({
    mutationFn: userManagementService.create,
    onSuccess: () => { toast.success('User created'); setModalOpen(false); invalidate(); },
    onError: () => toast.error('Failed to create user'),
  });

  const suspendMutation = useMutation({
    mutationFn: (u: OrgUser) => u.isActive ? userManagementService.suspend(u.id) : userManagementService.activate(u.id),
    onSuccess: () => { toast.success('User updated'); invalidate(); },
    onError: () => toast.error('Failed to update user'),
  });

  const deleteMutation = useMutation({
    mutationFn: userManagementService.remove,
    onSuccess: () => { toast.success('User deleted'); invalidate(); },
    onError: () => toast.error('Cannot delete this user'),
  });

  const handleDelete = async (u: OrgUser) => {
    if (await confirmDelete(`${u.firstName} ${u.lastName}`)) deleteMutation.mutate(u.id);
  };

  const handleSearch = (v: string) => { setSearch(v); setPage(1); };

  if (isLoading) return <SkeletonPage />;
  // Audit F-2 — a refusal is not an absence. Without this a 403 falls through
  // to the empty state, which reads as a fact about the tenant.
  if (isForbiddenError(error)) return <AccessRestricted />;

  return (
    <div className="space-y-5">
      <PageHeader
        title="Users"
        description="Manage tenant users and their roles"
        onAdd={can('user:create') ? () => setModalOpen(true) : undefined}
        addLabel="Add User"
      />

      <SearchBar value={search} onChange={handleSearch} placeholder="Search by name, email, or employee code..." />

      {data && data.total > 0 ? (
        <>
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full min-w-[700px] text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/40 text-left">
                  <th className="px-4 py-3 font-medium text-foreground">User</th>
                  <th className="px-4 py-3 font-medium text-foreground">Employee Code</th>
                  <th className="px-4 py-3 font-medium text-foreground">Department</th>
                  <th className="px-4 py-3 font-medium text-foreground">Roles</th>
                  <th className="px-4 py-3 font-medium text-foreground">Status</th>
                  <th className="px-4 py-3 font-medium text-foreground sr-only">Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((u) => (
                  <tr key={u.id} className="border-b border-border last:border-0 hover:bg-muted/20">
                    <td className="px-4 py-3">
                      <div className="font-medium text-foreground">{u.firstName} {u.lastName}</div>
                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <span className="truncate">{u.email}</span>
                        <SharedMailboxBadge
                          isShared={u.isSharedEmailAccount}
                          userCount={u.sharedEmailUserCount}
                        />
                      </div>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{u.employeeCode ?? '—'}</td>
                    <td className="px-4 py-3 text-muted-foreground">{u.department?.name ?? '—'}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {u.roles.map((r) => (
                          <span key={r.id} className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">{r.name}</span>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${u.isActive ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'}`}>
                        {u.isActive ? 'Active' : 'Suspended'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => navigate(ROUTES.ORG_USER_DETAIL.replace(':id', u.id))}
                          className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                          aria-label={`View ${u.firstName}`}
                        >
                          <FontAwesomeIcon icon={faEye} className="h-3.5 w-3.5" />
                        </button>
                        {can('user:suspend') && (
                          <button
                            onClick={() => suspendMutation.mutate(u)}
                            className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                            aria-label={u.isActive ? 'Suspend' : 'Activate'}
                            disabled={suspendMutation.isPending}
                          >
                            <FontAwesomeIcon icon={u.isActive ? faToggleOn : faToggleOff} className="h-4 w-4" />
                          </button>
                        )}
                        {can('user:delete') && (
                          <button
                            onClick={() => handleDelete(u)}
                            className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
                            aria-label={`Delete ${u.firstName}`}
                            disabled={deleteMutation.isPending}
                          >
                            <FontAwesomeIcon icon={faTrash} className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination page={data.page} pageSize={data.pageSize} total={data.total} onPageChange={setPage} />
        </>
      ) : (
        <EmptyState
          icon={faUsers}
          title={search ? 'No users match your search' : 'No users yet'}
          description={search ? 'Try a different search term.' : 'Add your first user to get started.'}
          action={!search && can('user:create') ? <Button onClick={() => setModalOpen(true)}>Add User</Button> : undefined}
        />
      )}

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title="Add User" size="lg">
        <CreateUserForm
          onSubmit={(payload) => createMutation.mutate(payload)}
          onCancel={() => setModalOpen(false)}
          saving={createMutation.isPending}
        />
      </Modal>
    </div>
  );
}
